import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Building2, User, MapPin, ReceiptIndianRupee, CreditCard, FileText, CheckCircle2 } from 'lucide-react';
import api from '../../utils/api';
import RichTextarea from '../../components/RichTextarea';
import Dropdown from '../../components/Dropdown';
import { getStateOptions, getCityOptions, getCitiesForState } from '../../utils/locationData';
import toast from 'react-hot-toast';

// Utility to convert number to words in Indian Rupees
function numberToWordsINR(amount) {
  if (amount === undefined || amount === null || isNaN(amount)) return '';
  const num = Math.abs(parseFloat(amount));
  if (num === 0) return 'Indian Rupees Zero Only';

  const singleDigits = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const twoDigits = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tenMultiples = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convertBelowThousand(n) {
    let str = '';
    if (n >= 100) {
      str += singleDigits[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      str += tenMultiples[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      str += twoDigits[n - 10] + ' ';
      n = 0;
    }
    if (n > 0) {
      str += singleDigits[n] + ' ';
    }
    return str.trim();
  }

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  let crores = Math.floor(integerPart / 10000000);
  let remainder = integerPart % 10000000;
  let lakhs = Math.floor(remainder / 100000);
  remainder = remainder % 100000;
  let thousands = Math.floor(remainder / 1000);
  remainder = remainder % 1000;
  let hundredsAndUnits = remainder;

  let words = '';
  if (crores > 0) words += convertBelowThousand(crores) + ' Crore ';
  if (lakhs > 0) words += convertBelowThousand(lakhs) + ' Lakh ';
  if (thousands > 0) words += convertBelowThousand(thousands) + ' Thousand ';
  if (hundredsAndUnits > 0) words += convertBelowThousand(hundredsAndUnits) + ' ';

  words = words.trim();
  let result = words ? `Indian Rupees ${words}` : 'Indian Rupees Zero';
  if (decimalPart > 0) {
    result += ` and ${convertBelowThousand(decimalPart)} Paise`;
  }
  result += ' Only';
  return result.replace(/\s+/g, ' ');
}

const UNIT_OPTIONS = [
  { value: 'Nos', label: 'Nos' },
  { value: 'Pcs', label: 'Pcs' },
  { value: 'Set', label: 'Set' },
  { value: 'Unit', label: 'Unit' },
  { value: 'Meter', label: 'Meter' },
  { value: 'Kg', label: 'Kg' }
];

const GST_OPTIONS = [
  { value: '0', label: '0%' },
  { value: '5', label: '5%' },
  { value: '12', label: '12%' },
  { value: '18', label: '18%' },
  { value: '28', label: '28%' }
];

const selectArrowStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  backgroundSize: '12px'
};

const InvoiceFormModal = ({ isOpen, onClose, invoiceToEdit = null, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [leadsList, setLeadsList] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [isOtherBillingState, setIsOtherBillingState] = useState(false);
  const [isOtherBillingCity, setIsOtherBillingCity] = useState(false);
  const [isOtherShippingState, setIsOtherShippingState] = useState(false);
  const [isOtherShippingCity, setIsOtherShippingCity] = useState(false);

  // Form State without PAN
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'Draft',
    leadId: null,

    // Abhay Agency details
    agencyName: 'Abhay Agency',
    agencyLogo: '/logo.png',
    agencyEmail: 'info@abhayagency.com',
    agencyPhone: '+91 98250 12345',
    agencyPhone2: '',
    agencyAddress: 'Shop No. 12, Ground Floor, Shreepal Complex, Ring Road',
    agencyCity: 'Surat',
    agencyState: 'Gujarat',
    agencyStateCode: '24',
    agencyPincode: '395002',
    agencyGst: '24AABCA1234F1Z5',
    agencyPan: 'AABCA1234F',

    // Customer Information (No PAN)
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerGst: '',

    // Billing Address
    billingStreet: '',
    billingCity: 'Surat',
    billingState: 'Gujarat',
    billingPincode: '',
    billingCountry: 'India',

    // Shipping Address
    isSameAsBilling: true,
    shippingName: '',
    shippingStreet: '',
    shippingCity: '',
    shippingState: '',
    shippingPincode: '',
    shippingCountry: 'India',

    // Items
    items: [
      {
        productCode: '',
        name: '',
        description: '',
        hsnCode: '8443',
        quantity: 1,
        unitPrice: 0,
        gstRate: 18
      }
    ],

    // Discount & Charges
    discountType: 'percentage',
    discountValue: 0,
    shippingCharges: 0,

    // Notes & Terms
    notes: 'Thank you for your valuable inquiry! Please make payments using the bank account details mentioned below.',
    termsAndConditions: [
      '1. This Proforma Invoice is valid for 15 days from the date of issuance.',
      '2. Prices quoted are inclusive/exclusive of GST as itemized above.',
      '3. Payment terms: 50% advance along with formal purchase order, balance before delivery/dispatch.',
      '4. Machine warranty and after-sales service as per manufacturer standard policy.',
      '5. Goods once dispatched/sold are non-returnable and non-refundable.',
      '6. Delivery timelines are subject to stock availability upon payment clearance.',
      '7. All disputes are strictly subject to Surat jurisdiction only.'
    ].join('\n'),

    // Bank Details
    showBankDetails: true,
    bankName: 'State Bank of India',
    accountName: 'Abhay Agency',
    accountNumber: '38291048591',
    accountType: 'Current Account',
    branchName: 'SURAT',
    ifscCode: 'SBIN0001234',

    // UPI Details
    showUpi: true,
    upiId: 'abhayagency@sbi',
    upiQrCode: '',

    // Signatory
    authorizedSignatoryLabel: 'For Abhay Agency',
    authorizedSignatoryName: 'Authorized Signatory'
  });

  // Fetch Agency Defaults & Leads list on Mount
  useEffect(() => {
    if (!isOpen) return;

    const fetchInitialData = async () => {
      try {
        const [defaultsRes, leadsRes] = await Promise.all([
          api.get('/proforma-invoices/defaults'),
          api.get('/leads?limit=250')
        ]);

        if (leadsRes.data) {
          setLeadsList(leadsRes.data.data || leadsRes.data || []);
        }

        if (invoiceToEdit) {
          setFormData({
            ...invoiceToEdit,
            showBankDetails: invoiceToEdit.showBankDetails !== undefined
              ? (invoiceToEdit.showBankDetails === true || invoiceToEdit.showBankDetails === 'true')
              : true,
            invoiceDate: invoiceToEdit.invoiceDate ? invoiceToEdit.invoiceDate.split('T')[0] : '',
            validUntil: invoiceToEdit.validUntil ? invoiceToEdit.validUntil.split('T')[0] : '',
            items: invoiceToEdit.items && invoiceToEdit.items.length > 0
              ? invoiceToEdit.items.map(it => ({
                  productCode: it.productCode || '',
                  name: it.name || '',
                  description: it.description || '',
                  hsnCode: it.hsnCode || '',
                  quantity: it.quantity || 1,
                  unitPrice: it.unitPrice || 0,
                  gstRate: it.gstRate !== undefined ? it.gstRate : 18
                }))
              : [{ productCode: '', name: '', description: '', hsnCode: '8443', quantity: 1, unitPrice: 0, gstRate: 18 }]
          });
          if (invoiceToEdit.leadId) {
            setSelectedLeadId(String(invoiceToEdit.leadId));
          }
        } else {
          const d = defaultsRes.data?.defaults || {};
          const nextNum = defaultsRes.data?.nextInvoiceNumber || '';
          setFormData({
            invoiceNumber: nextNum,
            invoiceDate: new Date().toISOString().split('T')[0],
            validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            status: 'Draft',
            leadId: null,

            // Abhay Agency details from Settings
            agencyName: d.agencyName || 'Abhay Agency',
            agencyLogo: d.agencyLogo || '/logo.png',
            agencyEmail: d.agencyEmail || 'info@abhayagency.com',
            agencyPhone: d.agencyPhone || '+91 98250 12345',
            agencyPhone2: d.agencyPhone2 || '',
            agencyAddress: d.agencyAddress || 'Shop No. 12, Ground Floor, Shreepal Complex, Ring Road',
            agencyCity: d.agencyCity || 'Surat',
            agencyState: d.agencyState || 'Gujarat',
            agencyStateCode: d.agencyStateCode || '24',
            agencyPincode: d.agencyPincode || '395002',
            agencyGst: d.agencyGst || '24AABCA1234F1Z5',
            agencyPan: d.agencyPan || 'AABCA1234F',

            // Customer Info
            customerName: '',
            customerEmail: '',
            customerPhone: '',
            customerGst: '',

            // Billing Address
            billingStreet: '',
            billingCity: 'Surat',
            billingState: 'Gujarat',
            billingPincode: '',
            billingCountry: 'India',

            // Shipping Address
            isSameAsBilling: true,
            shippingName: '',
            shippingStreet: '',
            shippingCity: '',
            shippingState: '',
            shippingPincode: '',
            shippingCountry: 'India',

            // Items
            items: [
              { productCode: '', name: '', description: '', hsnCode: '8443', quantity: 1, unitPrice: 0, gstRate: 18 }
            ],

            // Bank Details from Settings
            bankName: d.bankName || 'State Bank of India',
            accountName: d.accountName || 'Abhay Agency',
            accountNumber: d.accountNumber || '38291048591',
            accountType: d.accountType || 'Current Account',
            branchName: d.branchName || 'SURAT',
            ifscCode: d.ifscCode || 'SBIN0001234',
            showBankDetails: d.showBankDetails !== undefined ? d.showBankDetails : true,

            // UPI Details from Settings
            showUpi: d.showUpi !== undefined ? d.showUpi : true,
            upiId: d.upiId || 'abhayagency@sbi',
            upiQrCode: d.upiQrCode || '',

            // Signatory
            authorizedSignatoryLabel: d.authorizedSignatoryLabel || 'For Abhay Agency',
            authorizedSignatoryName: d.authorizedSignatoryName || 'Authorized Signatory',
            termsAndConditions: d.defaultTerms || '',
            notes: d.defaultNotes || ''
          });
          setSelectedLeadId('');
        }
      } catch (err) {
        console.error('Failed to load initial invoice data', err);
      }
    };

    fetchInitialData();
  }, [isOpen, invoiceToEdit]);

  // Handle lead selection auto-fill
  const handleLeadSelect = (leadId) => {
    setSelectedLeadId(leadId);

    if (!leadId) {
      setFormData(prev => ({ ...prev, leadId: null }));
      return;
    }

    const found = leadsList.find(l => String(l.id) === String(leadId));
    if (found) {
      setFormData(prev => ({
        ...prev,
        leadId: parseInt(leadId, 10),
        customerName: found.companyName || found.contactPerson || prev.customerName,
        customerPhone: found.phone || found.contactPhone || prev.customerPhone,
        customerEmail: found.email || found.contactEmail || prev.customerEmail,
        billingStreet: found.address || prev.billingStreet,
        billingCity: found.city || prev.billingCity,
        billingState: found.state || prev.billingState,
        billingPincode: found.pincode || prev.billingPincode,
        items: found.machine?.name ? [
          {
            name: `${found.machine.name} ${found.machineModel ? '- ' + found.machineModel : ''}`,
            description: '',
            hsnCode: '8443',
            unit: 'Nos',
            quantity: 1,
            unitPrice: 50000,
            gstRate: 18
          }
        ] : prev.items
      }));
      toast.success(`Loaded details for ${found.companyName || found.contactPerson}`);
    }
  };

  const handleBillingStateChange = (val) => {
    if (val === 'Other') {
      setIsOtherBillingState(true);
      setIsOtherBillingCity(true);
      setFormData(prev => ({
        ...prev,
        billingState: 'Other',
        billingCity: 'Other',
        ...(prev.isSameAsBilling ? { shippingState: 'Other', shippingCity: 'Other' } : {})
      }));
    } else {
      setIsOtherBillingState(false);
      const availableCities = getCitiesForState(val);
      const currentCity = formData.billingCity;
      const nextCity = (availableCities.includes(currentCity) && currentCity !== 'Other')
        ? currentCity
        : (availableCities[0] && availableCities[0] !== 'Other' ? availableCities[0] : '');
      setFormData(prev => ({
        ...prev,
        billingState: val,
        billingCity: nextCity,
        ...(prev.isSameAsBilling ? { shippingState: val, shippingCity: nextCity } : {})
      }));
      setIsOtherBillingCity(false);
    }
  };

  const handleBillingCityChange = (val) => {
    if (val === 'Other') {
      setIsOtherBillingCity(true);
      setFormData(prev => ({
        ...prev,
        billingCity: 'Other',
        ...(prev.isSameAsBilling ? { shippingCity: 'Other' } : {})
      }));
    } else {
      setIsOtherBillingCity(false);
      setFormData(prev => ({
        ...prev,
        billingCity: val,
        ...(prev.isSameAsBilling ? { shippingCity: val } : {})
      }));
    }
  };

  const handleShippingStateChange = (val) => {
    if (val === 'Other') {
      setIsOtherShippingState(true);
      setIsOtherShippingCity(true);
      setFormData(prev => ({
        ...prev,
        shippingState: 'Other',
        shippingCity: 'Other'
      }));
    } else {
      setIsOtherShippingState(false);
      const availableCities = getCitiesForState(val);
      const currentCity = formData.shippingCity;
      const nextCity = (availableCities.includes(currentCity) && currentCity !== 'Other')
        ? currentCity
        : (availableCities[0] && availableCities[0] !== 'Other' ? availableCities[0] : '');
      setFormData(prev => ({
        ...prev,
        shippingState: val,
        shippingCity: nextCity
      }));
      setIsOtherShippingCity(false);
    }
  };

  const handleShippingCityChange = (val) => {
    if (val === 'Other') {
      setIsOtherShippingCity(true);
      setFormData(prev => ({
        ...prev,
        shippingCity: 'Other'
      }));
    } else {
      setIsOtherShippingCity(false);
      setFormData(prev => ({
        ...prev,
        shippingCity: val
      }));
    }
  };

  // Item management
  const handleItemChange = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, items: updated }));
  };

  const addItemRow = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productCode: '',
          name: '',
          description: '',
          hsnCode: '8443',
          quantity: 1,
          unitPrice: 0,
          gstRate: 18
        }
      ]
    }));
  };

  const removeItemRow = (index) => {
    if (formData.items.length <= 1) {
      toast.error('Invoice must contain at least one item');
      return;
    }
    const updated = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updated }));
  };

  // Calculations
  const isIntraState = !formData.billingState ||
    (formData.agencyState || '').trim().toLowerCase() === (formData.billingState || '').trim().toLowerCase();

  const calculatedItems = formData.items.map(item => {
    const qty = Math.max(0, parseFloat(item.quantity) || 0);
    const rate = Math.max(0, parseFloat(item.unitPrice) || 0);
    const gstRate = Math.max(0, parseFloat(item.gstRate) || 0);
    const taxable = Math.round(qty * rate * 100) / 100;
    const gstAmt = Math.round(taxable * (gstRate / 100) * 100) / 100;
    const total = Math.round((taxable + gstAmt) * 100) / 100;
    return { taxable, gstAmt, total };
  });

  const subtotal = calculatedItems.reduce((acc, it) => acc + it.taxable, 0);
  const totalGst = calculatedItems.reduce((acc, it) => acc + it.gstAmt, 0);

  const cgstTotal = isIntraState ? Math.round((totalGst / 2) * 100) / 100 : 0;
  const sgstTotal = isIntraState ? Math.round((totalGst - cgstTotal) * 100) / 100 : 0;
  const igstTotal = !isIntraState ? totalGst : 0;

  const numDiscVal = Math.max(0, parseFloat(formData.discountValue) || 0);
  const discountAmount = formData.discountType === 'percentage'
    ? Math.round(subtotal * (numDiscVal / 100) * 100) / 100
    : Math.min(subtotal, Math.round(numDiscVal * 100) / 100);

  const shippingCharges = Math.max(0, parseFloat(formData.shippingCharges) || 0);
  const rawGrandTotal = subtotal + totalGst - discountAmount + shippingCharges;
  const grandTotal = Math.round(rawGrandTotal);
  const roundOff = Math.round((grandTotal - rawGrandTotal) * 100) / 100;
  const amountInWords = numberToWordsINR(grandTotal);

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (!formData.items.some(it => it.name && it.name.trim())) {
      toast.error('Please enter at least one item name');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        ...(formData.isSameAsBilling ? {
          shippingName: formData.customerName,
          shippingStreet: formData.billingStreet,
          shippingCity: formData.billingCity,
          shippingState: formData.billingState,
          shippingPincode: formData.billingPincode,
          shippingCountry: formData.billingCountry
        } : {}),
        showBankDetails: Boolean(formData.showBankDetails),
        subtotal,
        totalTax: totalGst,
        cgstTotal,
        sgstTotal,
        igstTotal,
        discountAmount,
        grandTotal,
        roundOff,
        totalInWords: amountInWords
      };

      if (invoiceToEdit && invoiceToEdit.id) {
        await api.put(`/proforma-invoices/${invoiceToEdit.id}`, payload);
        toast.success(`Proforma Invoice ${formData.invoiceNumber} updated successfully!`);
      } else {
        await api.post('/proforma-invoices', payload);
        toast.success(`Proforma Invoice ${formData.invoiceNumber} created successfully!`);
      }

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving invoice:', err);
      toast.error(err.response?.data?.message || 'Failed to save proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 10000, overflowY: 'auto', padding: '1.5rem 1rem' }}>
      <div
        className="modal-content fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '1080px',
          width: '100%',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}
      >
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.1rem 1.75rem',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ReceiptIndianRupee size={22} color="var(--color-primary)" />
              {invoiceToEdit ? `Edit Proforma Invoice: ${formData.invoiceNumber}` : 'Create Proforma Invoice'}
            </h2>
            <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              Abhay Agency GST-compliant proforma invoice generator
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: '1.5rem 1.75rem' }}>
          
          {/* Top Bar: Invoice Number, Date, Valid Until, Status */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            padding: '1rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                Invoice Number <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-field"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                placeholder="e.g. P2062"
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                Invoice Date <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="date"
                className="input-field"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                Invoice Status
              </label>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{ ...selectArrowStyle, paddingRight: '26px' }}
              >
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Converted">Converted</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Customer Information (PAN REMOVED) */}
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-primary)', marginBottom: '1.25rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.75rem' }}>
              <User size={18} color="var(--color-primary)" />
              Customer Information
            </div>

            {/* Clean 4-Column Balanced Grid without PAN */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                  Customer Name <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="e.g. Balaji Steel Pvt Ltd"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                  placeholder="e.g. +91 98765 43210"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="e.g. contact@customer.com"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '5px' }}>
                  GSTIN (GST Number)
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.customerGst}
                  onChange={(e) => setFormData({ ...formData, customerGst: e.target.value })}
                  placeholder="24ABCDE1234F1Z5"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Billing & Shipping Address */}
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-primary)', marginBottom: '1rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.75rem' }}>
              <MapPin size={18} color="var(--color-primary)" />
              Billing & Shipping Address
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Billing Address Column */}
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Billing Address
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Street Address / Area</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.billingStreet}
                      onChange={(e) => setFormData({ ...formData, billingStreet: e.target.value })}
                      placeholder="e.g. Plot No. 12, GIDC"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>City</label>
                      <Dropdown
                        placeholder="Select City..."
                        searchable={true}
                        value={formData.billingCity}
                        onChange={handleBillingCityChange}
                        options={getCityOptions(formData.billingState, formData.billingCity)}
                      />
                      {isOtherBillingCity && (
                        <input
                          type="text"
                          className="input-field"
                          placeholder="Enter City Name"
                          value={formData.billingCity === 'Other' ? '' : formData.billingCity}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            billingCity: e.target.value,
                            ...(prev.isSameAsBilling ? { shippingCity: e.target.value } : {})
                          }))}
                          style={{ marginTop: '0.35rem' }}
                        />
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>State</label>
                      <Dropdown
                        placeholder="Select State..."
                        searchable={true}
                        value={formData.billingState}
                        onChange={handleBillingStateChange}
                        options={getStateOptions(formData.billingState)}
                      />
                      {isOtherBillingState && (
                        <input
                          type="text"
                          className="input-field"
                          placeholder="Enter State Name"
                          value={formData.billingState === 'Other' ? '' : formData.billingState}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            billingState: e.target.value,
                            ...(prev.isSameAsBilling ? { shippingState: e.target.value } : {})
                          }))}
                          style={{ marginTop: '0.35rem' }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Pincode</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.billingPincode}
                        onChange={(e) => setFormData({ ...formData, billingPincode: e.target.value })}
                        placeholder="e.g. 395002"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Country</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.billingCountry}
                        onChange={(e) => setFormData({ ...formData, billingCountry: e.target.value })}
                        placeholder="India"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address Column */}
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Shipping Address
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: 'var(--color-primary)', cursor: 'pointer', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={formData.isSameAsBilling}
                      onChange={(e) => setFormData({ ...formData, isSameAsBilling: e.target.checked })}
                    />
                    Same as Billing
                  </label>
                </div>

                {formData.isSameAsBilling ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic', border: '1px dashed #cbd5e1', borderRadius: '6px', minHeight: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Shipping address is set to match the billing address.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Attention / Contact Person</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.shippingName}
                        onChange={(e) => setFormData({ ...formData, shippingName: e.target.value })}
                        placeholder="e.g. Warehouse In-charge"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Shipping Address</label>
                      <input
                        type="text"
                        className="input-field"
                        value={formData.shippingStreet}
                        onChange={(e) => setFormData({ ...formData, shippingStreet: e.target.value })}
                        placeholder="Delivery Site / Warehouse"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>City</label>
                        <Dropdown
                          placeholder="Select City..."
                          searchable={true}
                          value={formData.shippingCity}
                          onChange={handleShippingCityChange}
                          options={getCityOptions(formData.shippingState, formData.shippingCity)}
                        />
                        {isOtherShippingCity && (
                          <input
                            type="text"
                            className="input-field"
                            placeholder="Enter City Name"
                            value={formData.shippingCity === 'Other' ? '' : formData.shippingCity}
                            onChange={(e) => setFormData(prev => ({ ...prev, shippingCity: e.target.value }))}
                            style={{ marginTop: '0.35rem' }}
                          />
                        )}
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>State</label>
                        <Dropdown
                          placeholder="Select State..."
                          searchable={true}
                          value={formData.shippingState}
                          onChange={handleShippingStateChange}
                          options={getStateOptions(formData.shippingState)}
                        />
                        {isOtherShippingState && (
                          <input
                            type="text"
                            className="input-field"
                            placeholder="Enter State Name"
                            value={formData.shippingState === 'Other' ? '' : formData.shippingState}
                            onChange={(e) => setFormData(prev => ({ ...prev, shippingState: e.target.value }))}
                            style={{ marginTop: '0.35rem' }}
                          />
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Pincode</label>
                        <input
                          type="text"
                          className="input-field"
                          value={formData.shippingPincode}
                          onChange={(e) => setFormData({ ...formData, shippingPincode: e.target.value })}
                          placeholder="Pincode"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '3px' }}>Country</label>
                        <input
                          type="text"
                          className="input-field"
                          value={formData.shippingCountry}
                          onChange={(e) => setFormData({ ...formData, shippingCountry: e.target.value })}
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Line Items Table (Clean, Regular Fonts) */}
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-primary)' }}>
                <ReceiptIndianRupee size={18} color="var(--color-primary)" />
                Invoice Line Items
              </div>
              <button
                type="button"
                onClick={addItemRow}
                className="btn btn-primary"
                style={{ fontSize: '0.82rem', padding: '0.4rem 0.85rem', gap: '6px' }}
              >
                <Plus size={16} /> Add Item
              </button>
            </div>

            {/* Table Container */}
            <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'center', width: '40px', color: '#64748b', fontWeight: 500 }}>#</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', width: '140px', color: '#64748b', fontWeight: 500 }}>Product Code</th>
                    <th style={{ padding: '10px 10px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>Item Name *</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', width: '95px', color: '#64748b', fontWeight: 500 }}>HSN/SAC</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', width: '80px', color: '#64748b', fontWeight: 500 }}>QTY</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', width: '145px', color: '#64748b', fontWeight: 500 }}>Unit Price (₹)</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', width: '95px', color: '#64748b', fontWeight: 500 }}>GST %</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', width: '145px', color: '#64748b', fontWeight: 500 }}>Total (₹)</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', width: '45px', color: '#64748b', fontWeight: 500 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => {
                    const calc = calculatedItems[index] || { taxable: 0, gstAmt: 0, total: 0 };
                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: index % 2 === 1 ? '#fafafa' : '#ffffff' }}>
                        
                        {/* Index */}
                        <td style={{ padding: '8px', textAlign: 'center', color: '#94a3b8', fontWeight: 400 }}>
                          {index + 1}
                        </td>

                        {/* Product Code */}
                        <td style={{ padding: '8px' }}>
                          <input
                            type="text"
                            className="input-field"
                            value={item.productCode || ''}
                            onChange={(e) => handleItemChange(index, 'productCode', e.target.value)}
                            placeholder="e.g. WC95Z18212"
                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', fontWeight: 400 }}
                          />
                        </td>

                        {/* Item Name - fills remaining space */}
                        <td style={{ padding: '8px 10px' }}>
                          <input
                            type="text"
                            className="input-field"
                            value={item.name}
                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                            placeholder="Product or service name"
                            required
                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', fontWeight: 400 }}
                          />
                        </td>

                        {/* HSN/SAC */}
                        <td style={{ padding: '8px' }}>
                          <input
                            type="text"
                            className="input-field"
                            value={item.hsnCode || ''}
                            onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                            placeholder="8443"
                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', textAlign: 'center', padding: '0 6px', fontWeight: 400 }}
                          />
                        </td>

                        {/* QTY */}
                        <td style={{ padding: '8px' }}>
                          <input
                            type="number"
                            min="0.01"
                            step="any"
                            className="input-field"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', textAlign: 'center', padding: '0 4px', fontWeight: 400 }}
                            required
                          />
                        </td>

                        {/* Unit Price (₹) */}
                        <td style={{ padding: '8px' }}>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="input-field"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', textAlign: 'right', padding: '0 8px', fontWeight: 400 }}
                            required
                          />
                        </td>

                        {/* GST % Select */}
                        <td style={{ padding: '8px' }}>
                          <select
                            className="input-field"
                            value={item.gstRate}
                            onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                            style={{
                              ...selectArrowStyle,
                              width: '100%',
                              boxSizing: 'border-box',
                              height: '36px',
                              padding: '0 20px 0 8px',
                              textAlign: 'center',
                              fontSize: '0.85rem',
                              fontWeight: 400
                            }}
                          >
                            {GST_OPTIONS.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                        </td>

                        {/* Total Amount */}
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500, color: 'var(--color-text-main)', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                          ₹&nbsp;{calc.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>

                        {/* Remove Button */}
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeItemRow(index)}
                            style={{
                              color: '#ef4444',
                              background: '#fef2f2',
                              border: '1px solid #fee2e2',
                              borderRadius: '6px',
                              width: '30px',
                              height: '30px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            title="Remove Item"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 5: Payment Summary & Tax Calculation */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            {/* Left: Discounts & Extra Charges */}
            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.75rem' }}>
                Discounts & Additional Charges
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>Discount Type</label>
                  <select
                    className="input-field"
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    style={{ ...selectArrowStyle, paddingRight: '24px' }}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>Discount Value</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="input-field"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>Shipping / Freight Charges (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="input-field"
                  value={formData.shippingCharges}
                  onChange={(e) => setFormData({ ...formData, shippingCharges: e.target.value })}
                  placeholder="0"
                />
              </div>

              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ffffff', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '0.8rem', color: '#475569' }}>
                <div><strong>Tax Classification:</strong> {isIntraState ? 'Intra-state (Gujarat) - 50% CGST + 50% SGST' : 'Inter-state - 100% IGST'}</div>
              </div>
            </div>

            {/* Right: Payment Summary Box */}
            <div style={{ background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '1.25rem', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', borderBottom: '2px solid var(--color-border)', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>
                Payment Summary
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Subtotal (Taxable Value):</span>
                  <strong>₹ {subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                </div>

                {discountAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                    <span>Discount ({formData.discountType === 'percentage' ? `${formData.discountValue}%` : 'Fixed'}):</span>
                    <strong>- ₹ {discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                  </div>
                )}

                {cgstTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>CGST:</span>
                    <span>₹ {cgstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {sgstTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>SGST:</span>
                    <span>₹ {sgstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {igstTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>IGST:</span>
                    <span>₹ {igstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {shippingCharges > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Shipping / Freight:</span>
                    <span>₹ {shippingCharges.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {roundOff !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)' }}>
                    <span>Round Off:</span>
                    <span>{roundOff > 0 ? '+' : ''}₹ {roundOff.toFixed(2)}</span>
                  </div>
                )}

                {/* Grand Total Bar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-primary)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  marginTop: '6px'
                }}>
                  <span>Grand Total:</span>
                  <span>₹ {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>

                {/* Amount in words */}
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: '4px' }}>
                  <strong>In Words:</strong> {amountInWords}
                </div>
              </div>
            </div>
          </div>

          {/* Section 6: Notes & Terms and Conditions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ background: '#ffffff', padding: '1.25rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '8px' }}>
                Customer Notes / Remarks
              </label>
              <RichTextarea
                minHeight="200px"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes for customer, dispatch instructions, warranty note..."
              />
            </div>

            <div style={{ background: '#ffffff', padding: '1.25rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '8px' }}>
                Terms & Conditions
              </label>
              <RichTextarea
                minHeight="200px"
                value={formData.termsAndConditions || ''}
                onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                placeholder="1. Validity...\n2. Payment terms..."
              />
            </div>
          </div>

          {/* Section 7: Bank Details Toggle */}
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.92rem', color: 'var(--color-primary)' }}>
              <CreditCard size={18} color="var(--color-primary)" />
              Bank Account Details (Optional Display on Invoice)
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--color-primary)', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={Boolean(formData.showBankDetails)}
                onChange={(e) => setFormData({ ...formData, showBankDetails: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              Display on Invoice
            </label>
          </div>

          {/* Modal Footer Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '1rem',
            borderTop: '1px solid var(--color-border)'
          }}>
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={loading}
              style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ minWidth: '170px' }}
            >
              {loading ? 'Saving...' : invoiceToEdit ? 'Update Proforma Invoice' : 'Create Proforma Invoice'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default InvoiceFormModal;
